import { getEnterprises } from 'APIs/enterprises/enterprises';
import { EnterpriseDetails } from 'APIs/enterprises/enterprises.types';
import { createMarketplace } from 'APIs/marketplaces/marketplaces';
import { enterpriseSlugs } from 'Constants/Constants';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import getApiErrorMessage from 'utils/getApiErrorMsg';

import CreateMarketplaceModal from './CreateMarketplaceModal';
import { CreateMarketplaceFormProps, toCreateMarketplaceRequest } from './types';

type Props = {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  postCreateMarketplace?: () => void;
};

const CreateMarketplace = ({ showModal, setShowModal, postCreateMarketplace }: Props) => {
  const { apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');
  const [successData, setSuccessData] = useState<{ id: string; apiKey: string } | null>(null);

  const fetchEnterprises = useCallback(
    async (filterText?: string): Promise<Array<EnterpriseDetails>> => {
      if (!apiAprilBaseUri) {
        return [];
      }

      try {
        const resp = await getEnterprises(apiAprilBaseUri, {
          page: 1,
          limit: 100,
          readableIdentifier: (filterText && !enterpriseSlugs.includes(filterText)
            ? [...enterpriseSlugs, filterText]
            : enterpriseSlugs
          ).join(','),
        });
        return resp.items;
      } catch (e) {
        toast.error(getApiErrorMessage(e));
        return [];
      }
    },
    [apiAprilBaseUri],
  );

  const handleCreateMarketplace = useCallback(
    async (formData: CreateMarketplaceFormProps) => {
      if (!apiAprilBaseUri) {
        return;
      }
      const payload = toCreateMarketplaceRequest(formData);
      setIsModalLoading(true);

      try {
        const resp = await createMarketplace(apiAprilBaseUri, payload);
        setSuccessData(resp);
        setModalErrorMsg('');
      } catch (e) {
        setModalErrorMsg(getApiErrorMessage(e));
      } finally {
        setIsModalLoading(false);
      }
    },
    [apiAprilBaseUri],
  );

  useEffect(() => {
    if (!showModal) {
      setModalErrorMsg('');
      if (successData) {
        toast.success('Marketplace created successfully');
        setSuccessData(null);
        postCreateMarketplace && postCreateMarketplace();
      }
    }
  }, [postCreateMarketplace, showModal, successData]);

  return (
    <>
      {showModal && (
        <CreateMarketplaceModal
          saveMarketplaceHandler={handleCreateMarketplace}
          modalErrorMsg={modalErrorMsg}
          isModalLoading={isModalLoading}
          setShowModal={setShowModal}
          fetchEnterprises={fetchEnterprises}
          successData={successData}
        />
      )}
    </>
  );
};

export default CreateMarketplace;
